import React, {forwardRef, useEffect, useImperativeHandle, useRef, useState} from 'react'

/**
 * Виджет Yandex SmartCaptcha без npm-зависимости: скрипт подгружается сам,
 * один раз на всё приложение, и переиспользуется всеми формами.
 *
 * Ключ берётся из REACT_APP_SMARTCAPTCHA_KEY. Если его нет — компонент ничего
 * не рисует, а формы не требуют токена. Это зеркалит SMARTCAPTCHA_ENABLED на
 * бэкенде и позволяет работать локально без капчи.
 */

type WidgetId = string

interface ISmartCaptcha {
    render: (container: HTMLElement, params: {
        sitekey: string
        callback?: (token: string) => void
        hl?: string
        invisible?: boolean
    }) => WidgetId
    reset: (widgetId?: WidgetId) => void
    destroy: (widgetId?: WidgetId) => void
    getResponse: (widgetId?: WidgetId) => string
}

declare global {
    interface Window {
        smartCaptcha?: ISmartCaptcha
        __smartCaptchaOnLoad?: () => void
    }
}

export const SMARTCAPTCHA_KEY = process.env.REACT_APP_SMARTCAPTCHA_KEY || ''

/** Настроена ли капча. Формы по этому флагу решают, ждать токен или нет. */
export const captchaEnabled = !!SMARTCAPTCHA_KEY

const SCRIPT_SRC = 'https://smartcaptcha.yandexcloud.net/captcha.js?render=onload&onload=__smartCaptchaOnLoad'

// Промис на уровне модуля: сколько бы форм ни смонтировалось, тег <script>
// вставляется ровно один раз.
let loader: Promise<ISmartCaptcha> | null = null

function loadSmartCaptcha(): Promise<ISmartCaptcha> {
    if (loader) {
        return loader
    }

    loader = new Promise<ISmartCaptcha>((resolve, reject) => {
        if (window.smartCaptcha) {
            resolve(window.smartCaptcha)
            return
        }

        window.__smartCaptchaOnLoad = () => {
            if (window.smartCaptcha) {
                resolve(window.smartCaptcha)
            } else {
                reject(new Error('SmartCaptcha загрузилась, но не объявила себя'))
            }
        }

        const script = document.createElement('script')
        script.src = SCRIPT_SRC
        script.defer = true
        script.onerror = () => {
            loader = null // дать следующей форме шанс попробовать заново
            reject(new Error('Не удалось загрузить проверку "я не робот"'))
        }
        document.head.appendChild(script)
    })

    return loader
}

export interface SmartCaptchaHandle {
    /** Погасить использованный токен и показать виджет заново. */
    reset: () => void
}

interface SmartCaptchaProps {
    /** Вызывается с токеном, когда проверка пройдена, и с '' при сбросе. */
    onToken: (token: string) => void
    sitekey?: string
    className?: string
}

export const SmartCaptcha = forwardRef<SmartCaptchaHandle, SmartCaptchaProps>(
    function SmartCaptcha({onToken, sitekey = SMARTCAPTCHA_KEY, className}, ref) {

        const containerRef = useRef<HTMLDivElement>(null)
        const widgetRef = useRef<WidgetId | null>(null)
        const [error, setError] = useState<string>('')

        // Держим свежий колбэк в ref: иначе пришлось бы перерисовывать виджет
        // на каждый ререндер формы.
        const onTokenRef = useRef(onToken)
        onTokenRef.current = onToken

        useImperativeHandle(ref, () => ({
            reset: () => {
                if (widgetRef.current !== null && window.smartCaptcha) {
                    window.smartCaptcha.reset(widgetRef.current)
                }
                onTokenRef.current('')
            }
        }))

        useEffect(() => {
            if (!sitekey) {
                return
            }

            let cancelled = false

            loadSmartCaptcha()
                .then((captcha) => {
                    if (cancelled || !containerRef.current) {
                        return
                    }
                    widgetRef.current = captcha.render(containerRef.current, {
                        sitekey,
                        hl: 'ru',
                        callback: (token: string) => onTokenRef.current(token),
                    })
                })
                .catch((e: Error) => {
                    if (!cancelled) {
                        setError(e.message)
                    }
                })

            return () => {
                cancelled = true
                if (widgetRef.current !== null && window.smartCaptcha) {
                    window.smartCaptcha.destroy(widgetRef.current)
                    widgetRef.current = null
                }
            }
        }, [sitekey])

        if (!sitekey) {
            return null
        }

        return (
            <div className={className}>
                <div ref={containerRef}/>
                {error && <div className={'text-danger small mt-1'}>{error}</div>}
            </div>
        )
    }
)
